import { Component, Suspense, useDeferredValue, useState, type ReactNode } from 'react'
import { createFileRoute, Link, stripSearchParams, useMatch } from '@tanstack/react-router'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import * as valibot from 'valibot'
import { ERROR_STATUSES, type ErrorStatus } from 'shared/error-status'
import { ERROR_RANGES, ERROR_SORTS, type ErrorRange, type ErrorSort } from 'shared/wide-event-logs'
import { ChevronDownIcon } from 'lucide-react'
import { trpc } from '@/main'
import { toast } from '@/lib/toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { SearchInput } from '@/components/ui/search-input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SegmentedControl, SegmentedTab } from '@/components/custom/segmented-control'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK, SettingsSection } from './-components/settings/settings-section'
import { AlertTriangleIcon } from './-components/settings/settings-icons'
import { ADMIN_TONE } from './-components/admin/admin-pages'
import { SuperAdminBoundary } from './-components/admin/super-admin-boundary'
import { SessionLink, TraceLink } from './-components/admin/traces-shared'
import { ERRORS_DESCRIPTION, ErrorsPanelSkeleton, ErrorsPending } from './-components/admin/admin-pending'
import { formatLogTime, LEVEL_STYLE, levelOf, StackTrace } from './-components/admin/wide-event-logs'

// The captured-errors triage screen, after Sentry's issues: each Postgres issue with its occurrences from ClickHouse
// (a sparkline over the range, events, users, releases), expanding to the stack, the trail that led to its latest
// occurrence, its recent occurrences with their traces and sessions, and the captured context.

const DEFAULT_SEARCH = {
  errorStatus: 'active',
  errorRange: '7d',
  errorSort: 'recent',
} as const

export const Route = createFileRoute('/_app/admin/errors')({
  // Triage status, range and order are search params: a triage view is linkable and keys the list on `_list` below.
  validateSearch: valibot.object({
    errorStatus: valibot.fallback(
      valibot.optional(valibot.picklist(ERROR_STATUSES), DEFAULT_SEARCH.errorStatus),
      DEFAULT_SEARCH.errorStatus,
    ),
    errorRange: valibot.fallback(
      valibot.optional(valibot.picklist(ERROR_RANGES), DEFAULT_SEARCH.errorRange),
      DEFAULT_SEARCH.errorRange,
    ),
    errorSort: valibot.fallback(
      valibot.optional(valibot.picklist(ERROR_SORTS), DEFAULT_SEARCH.errorSort),
      DEFAULT_SEARCH.errorSort,
    ),
    // The expanded issue's fingerprint, so an open issue is a link and survives a reload. The list does not key on it.
    issue: valibot.fallback(
      valibot.optional(valibot.pipe(valibot.string(), valibot.regex(/^[0-9a-f]{64}$/))),
      undefined,
    ),
  }),
  search: { middlewares: [stripSearchParams(DEFAULT_SEARCH)] },
  component: RouteComponent,
  pendingComponent: ErrorsPending,
  staticData: { fullWidth: true },
})

type AdminOutput = inferRouterOutputs<AppRouter>['admin']
type Issue = AdminOutput['listErrorIssues']['issues'][number]
type IssueDetail = AdminOutput['getErrorIssue']

const STATUS_LABEL: Record<ErrorStatus, string> = {
  active: 'Active',
  ignored: 'Ignored',
  resolved: 'Resolved',
}
const RANGE_LABEL: Record<ErrorRange, string> = {
  '24h': '24h',
  '7d': '7d',
  '30d': '30d',
}
const SORT_LABEL: Record<ErrorSort, string> = {
  recent: 'Last seen',
  events: 'Most events',
  users: 'Most users',
  new: 'Newest',
}

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection icon={AlertTriangleIcon} tone={ADMIN_TONE} title="Errors" description={ERRORS_DESCRIPTION}>
        <SuperAdminBoundary what="errors">
          <Suspense fallback={<ErrorsPanelSkeleton />}>
            <ErrorsPanel />
          </Suspense>
        </SuperAdminBoundary>
      </SettingsSection>
    </div>
  )
}

function ErrorsPanel() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { context } = useMatch({ from: '/_app/admin/errors/_list' })
  const [find, setFind] = useState('')
  // Deferred so a status, range or order switch keeps the previous rows on screen while the new ones load.
  const deferred = useDeferredValue(context.errorIssuesQueryOptions)
  const { data } = useSuspenseQuery(deferred)
  const stale = deferred !== context.errorIssuesQueryOptions

  const term = find.trim().toLowerCase()
  const issues = term
    ? data.issues.filter((e) => `${e.errorType} ${e.message} ${e.route}`.toLowerCase().includes(term))
    : data.issues
  const events = data.issues.reduce((n, e) => n + (e.stats?.events ?? 0), 0)
  const fresh = data.issues.filter((e) => new Date(e.firstSeen).toISOString() >= data.from).length
  const regressions = data.issues.filter((e) => e.effectiveStatus !== e.status).length

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl>
          {ERROR_STATUSES.map((status) => (
            <SegmentedTab
              key={status}
              from={Route.fullPath}
              search={(prev) => ({ ...prev, errorStatus: status })}
              active={search.errorStatus === status}
              replace
            >
              {STATUS_LABEL[status]}
            </SegmentedTab>
          ))}
        </SegmentedControl>
        <SegmentedControl>
          {ERROR_RANGES.map((range) => (
            <SegmentedTab
              key={range}
              from={Route.fullPath}
              search={(prev) => ({ ...prev, errorRange: range })}
              active={search.errorRange === range}
              replace
            >
              {RANGE_LABEL[range]}
            </SegmentedTab>
          ))}
        </SegmentedControl>
        <NativeSelect
          size="sm"
          aria-label="Order"
          value={search.errorSort}
          onChange={(event) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                errorSort: event.target.value as ErrorSort,
              }),
              replace: true,
            })
          }
        >
          {ERROR_SORTS.map((sort) => (
            <NativeSelectOption key={sort} value={sort}>
              {SORT_LABEL[sort]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <div className="w-64 max-w-full">
          <SearchInput value={find} onChange={setFind} placeholder="Type, message or route…" className="h-9" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Issues" value={data.issues.length} />
        <Stat label={`Events · ${RANGE_LABEL[search.errorRange]}`} value={data.store === 'ok' ? events : null} />
        <Stat label={`New · ${RANGE_LABEL[search.errorRange]}`} value={fresh} />
        <Stat label="Regressions" value={regressions} />
      </div>

      {data.store !== 'ok' && (
        <p
          className="text-muted-foreground rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs"
          role="status"
        >
          {data.store === 'disabled'
            ? 'Wide events are off here, so the issues show without their occurrences, sparklines and users.'
            : 'ClickHouse didn’t answer: the issues show without their occurrences, sparklines and users.'}
        </p>
      )}

      {issues.length === 0 ? (
        <p className="text-muted-foreground rounded-xl border p-6 text-sm">
          {term ? 'No issues match your search.' : `No ${search.errorStatus} issues.`}
        </p>
      ) : (
        <div className={cn('overflow-hidden rounded-xl border transition-opacity', stale && 'opacity-60')}>
          <div
            aria-hidden
            className="bg-muted/50 text-muted-foreground hidden grid-cols-[minmax(0,1fr)_9rem_4.5rem_4.5rem_9.5rem] gap-4 border-b px-3 py-2 text-[11px] font-medium tracking-wide uppercase md:grid"
          >
            <span>Issue</span>
            <span>Occurrences · {RANGE_LABEL[search.errorRange]}</span>
            <span className="text-right">Events</span>
            <span className="text-right">Users</span>
            <span>Triage</span>
          </div>
          <ul className="flex flex-col divide-y" aria-label="Issues">
            {issues.map((issue) => (
              <IssueRow key={issue.fingerprint} issue={issue} open={search.issue === issue.fingerprint} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Stat(props: { label: string; value: number | null }) {
  return (
    <div className="bg-card flex flex-col gap-1 rounded-lg border p-3">
      <span className="text-muted-foreground text-xs">{props.label}</span>
      <span className="text-2xl font-semibold tabular-nums">
        {props.value === null ? '—' : props.value.toLocaleString('en-US')}
      </span>
    </div>
  )
}

const STATUS_VARIANT: Record<ErrorStatus, 'destructive' | 'secondary' | 'outline'> = {
  active: 'destructive',
  ignored: 'outline',
  resolved: 'secondary',
}

function IssueRow(props: { issue: Issue; open: boolean }) {
  const issue = props.issue
  const open = props.open
  const regressed = issue.effectiveStatus !== issue.status
  return (
    <li className={cn(open && 'bg-muted/20')}>
      <div className="grid grid-cols-1 items-center gap-x-4 gap-y-2 px-3 py-2.5 md:grid-cols-[minmax(0,1fr)_9rem_4.5rem_4.5rem_9.5rem]">
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            from={Route.fullPath}
            search={(prev) => ({ ...prev, issue: open ? undefined : issue.fingerprint })}
            replace
            resetScroll={false}
            aria-expanded={open}
            className="flex min-w-0 items-start gap-2 text-left"
          >
            <ChevronDownIcon
              className={cn(
                'text-muted-foreground mt-0.5 size-3.5 shrink-0 transition-transform',
                open && 'rotate-180',
              )}
            />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="text-destructive font-mono text-xs font-semibold">{issue.errorType}</span>
                <Badge variant="secondary" className="text-[10px]">
                  {issue.origin}
                </Badge>
                {regressed && (
                  <Badge variant="destructive" className="text-[10px]">
                    regressed
                  </Badge>
                )}
                {issue.effectiveStatus !== 'active' && (
                  <Badge variant={STATUS_VARIANT[issue.effectiveStatus]} className="text-[10px]">
                    {issue.effectiveStatus}
                  </Badge>
                )}
              </span>
              <span className="line-clamp-2 text-xs break-words">{issue.message}</span>
            </span>
          </Link>
          <span className="text-muted-foreground flex flex-wrap gap-x-3 pl-5.5 text-[11px]">
            <span className="font-mono">{issue.route}</span>
            <span>first {formatLogTime(new Date(issue.firstSeen).toISOString()).slice(0, 15)}</span>
            <span>last {formatLogTime(new Date(issue.lastSeen).toISOString()).slice(0, 15)}</span>
            {issue.stats?.lastRelease && <span className="font-mono">{issue.stats.lastRelease}</span>}
            {issue.githubIssueUrl && (
              <a href={issue.githubIssueUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                #{issue.githubIssueNumber}
              </a>
            )}
          </span>
        </div>
        <Sparkline values={issue.stats?.sparkline ?? null} />
        <span className="text-right text-sm tabular-nums">
          {issue.stats ? issue.stats.events.toLocaleString('en-US') : <span className="text-muted-foreground">—</span>}
          <span className="text-muted-foreground block text-[11px]">×{issue.count} all time</span>
        </span>
        <span className="text-right text-sm tabular-nums">
          {issue.stats ? issue.stats.users.toLocaleString('en-US') : <span className="text-muted-foreground">—</span>}
        </span>
        <TriageActions issue={issue} />
      </div>
      {open && (
        <div className="px-3 pb-4 md:pl-8">
          <DetailBoundary>
            <Suspense fallback={<IssueDetailSkeleton />}>
              <IssueDetailView issue={issue} />
            </Suspense>
          </DetailBoundary>
        </div>
      )}
    </li>
  )
}

/** Occurrences per bar over the range, oldest left; scaled to the issue's own peak, so its shape reads, not its size. */
function Sparkline(props: { values: number[] | null }) {
  if (!props.values) return <span className="text-muted-foreground text-xs">—</span>
  const max = Math.max(1, ...props.values)
  const total = props.values.reduce((a, b) => a + b, 0)
  return (
    <svg
      viewBox={`0 0 ${props.values.length * 4} 24`}
      preserveAspectRatio="none"
      className="h-6 w-36"
      role="img"
      aria-label={`${total} occurrences over the range`}
    >
      <line x1={0} x2={props.values.length * 4} y1={23.5} y2={23.5} className="stroke-border" strokeWidth={1} />
      {props.values.map((value, i) =>
        value > 0 ? (
          <rect
            // oxlint-disable-next-line react/no-array-index-key -- a fixed series of buckets
            key={i}
            x={i * 4 + 0.5}
            y={24 - Math.max(2, (value / max) * 22)}
            width={3}
            height={Math.max(2, (value / max) * 22)}
            className={LEVEL_STYLE.error.fill}
          />
        ) : null,
      )}
    </svg>
  )
}

/** Resolve and ignore from active; reopen from either. A resolved issue that recurs reopens on its own. */
function TriageActions(props: { issue: Issue }) {
  const setStatus = useMutation(
    trpc.admin.setErrorStatus.mutationOptions({
      onError: () => toast.error('Couldn’t update the error status.'),
    }),
  )
  const set = (status: ErrorStatus) => setStatus.mutate({ fingerprint: props.issue.fingerprint, status })
  const status = props.issue.effectiveStatus
  return (
    <div className="flex items-center gap-1">
      {status === 'active' ? (
        <>
          <Button size="xs" variant="outline" disabled={setStatus.isPending} onClick={() => set('resolved')}>
            Resolve
          </Button>
          <Button size="xs" variant="ghost" disabled={setStatus.isPending} onClick={() => set('ignored')}>
            Ignore
          </Button>
        </>
      ) : (
        <Button size="xs" variant="outline" disabled={setStatus.isPending} onClick={() => set('active')}>
          Reopen
        </Button>
      )}
    </div>
  )
}

class DetailBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  override render() {
    if (this.state.failed) return <p className="text-destructive text-xs">Couldn’t load this issue’s occurrences.</p>
    return this.props.children
  }
}

function IssueDetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-40 w-full rounded-lg" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-48 rounded-lg" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    </div>
  )
}

function IssueDetailView(props: { issue: Issue }) {
  const issue = props.issue
  // query: direct keyed on the expanded issue, a search param the list layout deliberately does not key on
  const { data } = useSuspenseQuery(trpc.admin.getErrorIssue.queryOptions({ fingerprint: issue.fingerprint }))
  const crumbs = issue.context?.breadcrumbs ?? []
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <Link
          to="/admin/logs"
          search={{ query: `fingerprint:${issue.fingerprint}`, range: '30d' }}
          className="text-primary hover:underline"
        >
          Every occurrence in Logs
        </Link>
        {data.occurrences[0]?.sessionId && (
          <Link
            to="/admin/logs"
            search={{
              view: 'console',
              query: `session:${data.occurrences[0].sessionId}`,
              range: '30d',
            }}
            className="text-primary hover:underline"
          >
            The latest session’s console
          </Link>
        )}
      </div>
      {issue.stack ? (
        <StackTrace stack={issue.stack} />
      ) : (
        <p className="text-muted-foreground font-mono text-xs">
          {issue.errorType}: {issue.message} (no stack captured)
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Trail detail={data} breadcrumbs={crumbs} />
        <Occurrences detail={data} />
      </div>
      <ContextGrid issue={issue} />
    </div>
  )
}

function Panel(props: { title: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h4 className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">{props.title}</h4>
      {props.children}
    </section>
  )
}

const offset = (from: string, to: string): string => {
  const seconds = (Date.parse(to) - Date.parse(from)) / 1000
  return seconds === 0 ? '0s' : `−${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`
}

/**
 * What led to the latest occurrence: the session's clicks, navigations, console lines and requests in the five
 * minutes before it, from the wide events; else the browser's own breadcrumbs captured with the error.
 */
function Trail(props: { detail: IssueDetail; breadcrumbs: { t: number; type: string; message: string }[] }) {
  const trail = props.detail.trail
  const errorAt = trail.at(-1)?.occurredAt
  if (trail.length > 0 && errorAt)
    return (
      <Panel title={`Trail · ${trail.length} ${trail.length === 1 ? 'event' : 'events'} before the latest`}>
        <ol className="flex max-h-80 flex-col overflow-auto rounded-lg border text-xs">
          {trail.map((entry) => {
            const level = levelOf(entry.level)
            return (
              <li
                key={entry.id}
                className="grid grid-cols-[3px_3.5rem_6rem_minmax(0,1fr)] items-baseline gap-2 border-b px-2 py-1 last:border-b-0"
              >
                <span className={cn('h-3 self-center rounded-full', LEVEL_STYLE[level].bar)} aria-hidden />
                <span className="text-muted-foreground text-right font-mono tabular-nums">
                  {offset(entry.occurredAt, errorAt)}
                </span>
                <span className="text-muted-foreground truncate font-mono">{entry.kind}</span>
                <span
                  className={cn('truncate font-mono', level === 'debug' && 'text-muted-foreground')}
                  title={entry.message}
                >
                  {entry.message}
                </span>
              </li>
            )
          })}
        </ol>
      </Panel>
    )
  if (props.breadcrumbs.length > 0)
    return (
      <Panel title="Breadcrumbs">
        <ol className="flex flex-col gap-1 rounded-lg border p-2">
          {props.breadcrumbs.map((b) => (
            <li key={`${String(b.t)}-${b.type}-${b.message}`} className="flex items-baseline gap-2 text-xs">
              <span className="text-muted-foreground shrink-0 font-mono tabular-nums">
                {new Date(b.t).toISOString().slice(11, 19)}
              </span>
              <Badge variant="outline" className="shrink-0 font-mono text-[10px]">
                {b.type}
              </Badge>
              <span className="text-muted-foreground font-mono break-words">{b.message}</span>
            </li>
          ))}
        </ol>
      </Panel>
    )
  return (
    <Panel title="Trail">
      <p className="text-muted-foreground rounded-lg border p-3 text-xs">
        {props.detail.store === 'ok'
          ? 'No session or trace events around the latest occurrence.'
          : 'The wide-events store didn’t answer, so there is no trail.'}
      </p>
    </Panel>
  )
}

function Occurrences(props: { detail: IssueDetail }) {
  const rows = props.detail.occurrences
  return (
    <Panel title={`Latest occurrences · ${rows.length}`}>
      {rows.length === 0 ? (
        <p className="text-muted-foreground rounded-lg border p-3 text-xs">
          {props.detail.store === 'ok'
            ? 'None recorded in the wide events: they began after this issue’s last sighting.'
            : 'The wide-events store didn’t answer.'}
        </p>
      ) : (
        <ul className="flex max-h-80 flex-col overflow-auto rounded-lg border text-xs">
          {rows.map((row) => (
            <li
              key={row.id}
              title={row.path ?? undefined}
              className="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-baseline gap-x-3 border-b px-2 py-1 last:border-b-0"
            >
              <span className="text-muted-foreground font-mono tabular-nums">
                {formatLogTime(row.occurredAt).slice(0, 15)}
              </span>
              <span className="text-muted-foreground truncate">
                {[
                  row.env,
                  row.release,
                  row.userId && `user ${row.userId}`,
                  row.companyId && `company ${row.companyId}`,
                  row.platform,
                  row.source,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <span className="flex items-center gap-2">
                {row.traceId && (
                  <TraceLink traceId={row.traceId} at={row.occurredAt}>
                    trace
                  </TraceLink>
                )}
                {row.sessionId && <SessionLink sessionId={row.sessionId} at={row.occurredAt} />}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

const CONTEXT_LABEL: Record<string, string> = { ip: 'IP address' }

const scalar = (v: unknown): string | null =>
  v == null ? null : typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : null

/** The row's own facts and the latest occurrence's captured context, generically: errors.service owns the keys. */
function ContextGrid(props: { issue: Issue }) {
  const issue = props.issue
  const entries: [string, string | null][] = [
    ['fingerprint', issue.fingerprint],
    ['signature', issue.signature],
    ['status', issue.status],
    ['occurrences (all time)', String(issue.count)],
    ['first_seen', new Date(issue.firstSeen).toISOString()],
    ['last_seen', new Date(issue.lastSeen).toISOString()],
    ['releases', issue.stats ? [issue.stats.firstRelease, issue.stats.lastRelease].filter(Boolean).join(' → ') : null],
    ['environments', issue.stats?.envs.join(', ') ?? null],
    ...Object.entries(issue.context ?? {})
      .filter(([key]) => key !== 'breadcrumbs')
      .map(([key, value]): [string, string | null] => [CONTEXT_LABEL[key] ?? key, scalar(value)]),
  ]
  return (
    <Panel title="Context">
      <dl className="grid grid-cols-[minmax(8rem,12rem)_minmax(0,1fr)] rounded-lg border text-xs">
        {entries
          .filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== '')
          .map(([key, value]) => (
            <div key={key} className="col-span-2 grid grid-cols-subgrid border-b px-2 py-1 last:border-b-0">
              <dt className="text-muted-foreground truncate font-mono">{key}</dt>
              <dd className="font-mono break-all">{value}</dd>
            </div>
          ))}
      </dl>
    </Panel>
  )
}
